import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import _ from 'lodash';
import { toLower } from 'lodash';
import { BehaviorSubject, Observable, ReplaySubject, Subscription } from 'rxjs';
import { StatusLabelEnum } from 'src/app/components/status-label/status-label.component';
import { CheckboxClicked } from 'src/app/components/table-row/table-row.component';
import { PaginationOperationEnum } from 'src/app/components/table/table.component';
import { Platform } from 'src/app/models/api';
import { EnvironmentModel, EnvironmentsUpdate } from 'src/app/models/api/governance/environment.model';
import { PolicyType } from 'src/app/models/api/governance/policy.model';
import { ITablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { EnvironmentsTableColumns } from 'src/app/models/tableColumns/environmentsColumns.model';
import { EnvironmentsViewModel } from 'src/app/models/viewModels/environmentsViewModel';
import { EnvironmentsHttpService } from 'src/app/services/httpServices/governance/environmentsHttpService/environments-http.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { InventoryHelper } from '../../inventory/helpers/inventoryHelper';

interface EnvironmentsState {
  selectedEnvsIds: Set<string>;
  otherPolicyEnvsIds: Set<string>;
  unselectedEnvsIds: Set<string>;
}

@Component({
  selector: 'app-environments',
  templateUrl: './environments.component.html',
  styleUrls: ['./environments.component.scss'],
})
export class EnvironmentsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public editable = true;
  @Input() public environments = signal<EnvironmentModel[]>([]);
  @Input() public currentPolicyId: string | null;
  @Input() public currentPolicyType: PolicyType;
  @Input() public integrationId: string;
  @Input() public platform: Platform;
  @Output() public updateEnvironments: EventEmitter<EnvironmentModel[]> = new EventEmitter<EnvironmentModel[]>();

  public changesWereDone = false;
  public noData = signal<boolean>(false);

  // table config
  public environmentsTableCols = EnvironmentsTableColumns;
  public tableRows: Observable<BehaviorSubject<EnvironmentsViewModel>[]>;
  public pageInfo$: BehaviorSubject<ITablePageInfo>;

  // environments names
  public environmentsDisplayNamesForSearch: string[] = [];
  public environmentsDisplayNamesToPresent: string[] = [];

  // loading behavior subject
  public isLoading = signal<boolean>(false);
  public resetSearchBoxBehavioralSubject$: ReplaySubject<string[] | undefined> = new ReplaySubject<
    string[] | undefined
  >();

  private _pageNumber = 1;
  private _areEnvironmentsReceived = false;

  // environment selection
  private readonly _currentState: EnvironmentsState = {
    selectedEnvsIds: new Set(),
    otherPolicyEnvsIds: new Set(),
    unselectedEnvsIds: new Set(),
  } as EnvironmentsState;

  private readonly _originalState: EnvironmentsState = {
    selectedEnvsIds: new Set(),
    otherPolicyEnvsIds: new Set(),
    unselectedEnvsIds: new Set(),
  } as EnvironmentsState;

  // Subscriptions
  private _modifyEnvironmentsSubscription$?: Subscription;
  private _getEnvironmentsSubscription$?: Subscription;

  constructor(
    private readonly _router: Router,
    private readonly _environmentsHttpService: EnvironmentsHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService
  ) {}

  ngOnInit(): void {
    this.noData.set(this.environments().length === 0);
    if (this.environments().length > 0) this._areEnvironmentsReceived = true;
    this._saveEnvironmentsState();

    this._initializeSearchComponent();

    this._initializePageInfo();

    this._buildEnvironmentsViewModel();

    this._handlePageInfo();
  }

  ngOnDestroy(): void {
    this._modifyEnvironmentsSubscription$?.unsubscribe();
    this._getEnvironmentsSubscription$?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['environments'] && !this._areEnvironmentsReceived && this.environments().length > 0) {
      this._areEnvironmentsReceived = true;
      this.ngOnInit();
    }
  }

  public setEnvironments() {
    this.isLoading.set(true);
    // update policy environments relations request
    if (this.currentPolicyId) {
      const envsToUpdate = {
        environmentsToAdd: this._getDifference(this._currentState.selectedEnvsIds, this._originalState.selectedEnvsIds),
        environmentsToDelete: this._getDifference(
          this._currentState.unselectedEnvsIds,
          this._originalState.unselectedEnvsIds
        ),
      } as EnvironmentsUpdate;

      this._modifyEnvironmentsSubscription$ = this._environmentsHttpService
        .updateEnvironments(this.integrationId, this.currentPolicyId, envsToUpdate)
        .subscribe(
          (errors) => {
            if (errors.length > 0) {
              this._notificationService.warning('Some of the environments got saved');
            } else {
              this._notificationService.success('Environments saved successfully');
            }
            this._updateEnvironments();
          },
          (error) => this._httpErrorHandlerService.handle(error)
        );
    }
  }

  public cancelEdit() {
    this._router.navigate(['app/policies'], {
      queryParams: { integration: this.integrationId },
    });
  }

  public resetForm() {
    this._resetCurrentState();

    this._buildEnvironmentsViewModel();

    this._checkIfChangesWereDone();

    this.resetSearchBoxBehavioralSubject$.next(undefined);
  }

  public onCheckboxClick(event: CheckboxClicked) {
    const envId: string = event.id;
    const status: boolean = event.status;

    if (status) {
      this._currentState.selectedEnvsIds.add(envId);
      this._currentState.unselectedEnvsIds.delete(envId);
    } else {
      this._currentState.unselectedEnvsIds.add(envId);
      this._currentState.selectedEnvsIds.delete(envId);
    }
    this._checkIfChangesWereDone();
  }

  public environmentSearched(environments: string[]) {
    this.environmentsDisplayNamesToPresent = environments;
    this._buildEnvironmentsViewModel();

    this._handlePageInfo();
  }

  public onPagination(operation: PaginationOperationEnum) {
    if (operation == PaginationOperationEnum.next) {
      this._pageNumber += 1;
    } else if (this._pageNumber > 1) {
      this._pageNumber -= 1;
    }
    this._handlePageInfo();

    this._buildEnvironmentsViewModel();
  }

  private _initializeSearchComponent() {
    // Clear the array first to prevent duplicates when method is called multiple times
    this.environmentsDisplayNamesForSearch = [];

    this.environments().forEach((env) => {
      this.environmentsDisplayNamesForSearch.push(env.environmentName);
    });
    this.environmentsDisplayNamesToPresent = _.cloneDeep(this.environmentsDisplayNamesForSearch);
  }

  private _updateEnvironments() {
    this._getEnvironmentsSubscription$ = this._environmentsHttpService
      .getEnvironments(this.integrationId)
      .subscribe((response) => {
        this.environments.set(response);
        this._resetAllStates();
        this._saveEnvironmentsState();
        this._buildEnvironmentsViewModel();
        this._checkIfChangesWereDone();
        this.isLoading.set(false);

        this.updateEnvironments.emit(this.environments());
      });
  }

  private _resetCurrentState() {
    this._currentState.selectedEnvsIds = _.cloneDeep(this._originalState.selectedEnvsIds);
    this._currentState.otherPolicyEnvsIds = _.cloneDeep(this._originalState.otherPolicyEnvsIds);
    this._currentState.unselectedEnvsIds = _.cloneDeep(this._originalState.unselectedEnvsIds);
  }

  private _resetAllStates() {
    this._originalState.selectedEnvsIds = new Set();
    this._originalState.otherPolicyEnvsIds = new Set();
    this._originalState.unselectedEnvsIds = new Set();
    this._currentState.selectedEnvsIds = new Set();
    this._currentState.otherPolicyEnvsIds = new Set();
    this._currentState.unselectedEnvsIds = new Set();
  }

  private _getDifference(setA: Set<string>, setB: Set<string>) {
    return [...setA].filter((element) => !setB.has(element));
  }

  private _checkIfChangesWereDone() {
    if (
      _.isEqual(this._originalState.selectedEnvsIds, this._currentState.selectedEnvsIds) &&
      _.isEqual(this._originalState.otherPolicyEnvsIds, this._currentState.otherPolicyEnvsIds) &&
      _.isEqual(this._originalState.unselectedEnvsIds, this._currentState.unselectedEnvsIds)
    ) {
      this.changesWereDone = false;
    } else this.changesWereDone = true;
  }

  private _saveEnvironmentsState() {
    const sortedEnvs: EnvironmentModel[] = [];
    this.environments().sort((env1, env2) => (toLower(env1.environmentName) > toLower(env2.environmentName) ? 1 : -1));

    // selected envs
    this.environments().forEach((env) => {
      if (env.policyId === this.currentPolicyId) {
        sortedEnvs.push(env);
        this._currentState.selectedEnvsIds.add(env.inventoryResourceId);
        this._originalState.selectedEnvsIds.add(env.inventoryResourceId);
      }
    });

    // default envs that can be selected
    this.environments().forEach((env) => {
      if (env.policyType === PolicyType.default && env.policyId !== this.currentPolicyId) {
        sortedEnvs.push(env);
        this._currentState.unselectedEnvsIds.add(env.inventoryResourceId);
        this._originalState.unselectedEnvsIds.add(env.inventoryResourceId);
      }
    });

    // all other envs
    this.environments().forEach((env) => {
      if (env.policyType !== PolicyType.default && env.policyId !== this.currentPolicyId) {
        sortedEnvs.push(env);
        this._currentState.otherPolicyEnvsIds.add(env.inventoryResourceId);
        this._originalState.otherPolicyEnvsIds.add(env.inventoryResourceId);
      }
    });

    this.environments.set(sortedEnvs);
  }

  private _buildEnvironmentsViewModel() {
    const viewModels: BehaviorSubject<EnvironmentsViewModel>[] = [];

    this.environments().forEach((env) => {
      if (this._isEnvironmentsShouldBePresented(env)) {
        const viewModel: EnvironmentsViewModel = {
          rowId: env.inventoryResourceId,
          clickable: false,
          environmentName: env.environmentName,
          environmentDesc: this._createEnvironmentDescription(env),
          createdBy: env.createdBy,
          createdAt: env.createdAt ?? 'N/A',
          resources: env.numberOfRelatedResources,
          checkboxStatus: this._checkIfCheckboxIsChecked(env),
          disabled: this._isCheckboxShouldBeDisabled(env),
          disabledCheckbox: this._isCheckboxShouldBeDisabled(env),
          statusLabel: env.status ? InventoryHelper.getResourceStatusLabel(env.status) : StatusLabelEnum.active,
        } as EnvironmentsViewModel;

        viewModels.push(new BehaviorSubject(viewModel));
      }
    });

    this.tableRows = new BehaviorSubject(viewModels);
  }

  private _createEnvironmentDescription(env: EnvironmentModel): string {
    if (env.policyId !== this.currentPolicyId && env.policyType !== PolicyType.default) {
      return `Associated with ${env.policyName}.\nRemove 1st to associate with a new policy`;
    } else return '';
  }

  private _isCheckboxShouldBeDisabled(env: EnvironmentModel): boolean {
    if (!this.editable) {
      return true;
    } else if (
      // environment that doesn't associated with any policy
      (env.policyId !== this.currentPolicyId && env.policyType === PolicyType.default) ||
      // environment that associated with the current policy which is not the default
      (env.policyId === this.currentPolicyId && env.policyType !== PolicyType.default)
    )
      return false;
    else return true;
  }

  private _checkIfCheckboxIsChecked(env: EnvironmentModel): boolean {
    // it the current policy is the default, show the other environemts that was selected by other policies as disabled and not selected in default policy.
    if (
      this._currentState.otherPolicyEnvsIds.has(env.inventoryResourceId) &&
      this.currentPolicyType === PolicyType.default
    ) {
      return false;
    } else if (
      this._currentState.selectedEnvsIds.has(env.inventoryResourceId) ||
      this._currentState.otherPolicyEnvsIds.has(env.inventoryResourceId)
    ) {
      return true;
    } else return false;
  }

  private _initializePageInfo() {
    this.pageInfo$ = new BehaviorSubject({
      number: 1,
      currSize: 0,
      maxSize: this._environmentsHttpService.defaultPageSize,
      totalItems: 0,
      totalPages: 1,
    });
  }

  private _handlePageInfo() {
    const totalItems = this.environmentsDisplayNamesToPresent.length;
    const curry =
      this.environmentsDisplayNamesToPresent.length % this._environmentsHttpService.defaultPageSize > 1 ? 1 : 0;
    const totalPages =
      Math.floor(this.environmentsDisplayNamesToPresent.length / this._environmentsHttpService.defaultPageSize) + curry;

    this.pageInfo$.next({
      number: this._pageNumber,
      currSize:
        this._pageNumber === totalPages
          ? totalItems % this._environmentsHttpService.defaultPageSize
          : this._environmentsHttpService.defaultPageSize,
      maxSize: this._environmentsHttpService.defaultPageSize,
      totalItems: totalItems,
      totalPages: totalPages,
    });
  }

  private _isEnvironmentsShouldBePresented(env: EnvironmentModel): boolean {
    // check if exist in environmentsDisplayNamesToPresent
    // check it exist on page number
    const MAX_PAGE_SIZE = this._environmentsHttpService.defaultPageSize;
    const elementsOnPage = this.environmentsDisplayNamesToPresent.slice(
      (this._pageNumber - 1) * MAX_PAGE_SIZE,
      (this._pageNumber - 1) * MAX_PAGE_SIZE + MAX_PAGE_SIZE
    );
    return elementsOnPage.includes(env.environmentName);
  }
}
